"use client";
import { useEffect, useState } from "react";
import { UserAuthForm } from "../../../components/user-auth-form";
import { OtpAuthForm } from "@/components/otp-auth-form";
import Link from "next/link";
import WebLogo from "@/components/common/WebLogo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle } from "lucide-react";
import { useSearchParams, useRouter } from "next/navigation";
import useAuthStore from "@/lib/useAuthStore";

export default function Login() {
  const [mounted, setMounted] = useState(false);
  const [sharedEmail, setSharedEmail] = useState("");
  const searchParams = useSearchParams();
  const isExpired = searchParams?.get("expired") === "true";
  const message = searchParams?.get("message");
  
  // Get impersonation parameters from URL
  const impersonationEmail = searchParams?.get("email");
  const impersonationCode = searchParams?.get("code");
  const isImpersonationLogin = !!(impersonationEmail && impersonationCode);
  
  const { isAuthenticated, logOutUser } = useAuthStore();
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleManualLogout = async () => {
    try {
      await logOutUser();
      
      // Force reload to clear any lingering state
      // Use timeout to allow state changes to complete
      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (error) {
      console.error("Error during manual logout:", error);
      // Fallback if logout fails
      window.location.reload();
    }
  };

  if (!mounted) {
    return null;
  }

  return (
    <>
      <div className="p-4 h-[100vh] flex items-center justify-center">
        <div className="py-10 flex flex-col items-center justify-center bg-white shadow-xl rounded-lg ">  
          <WebLogo />
          <div className={`flex items-center justify-center`}>
            <div className="mx-auto flex flex-col justify-center space-y-2 w-[75%]">
              {isExpired && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your session has expired due to inactivity. Please log in again.
                  </AlertDescription>
                </Alert>
              )}

              {message === "password-changed" && (
                <Alert className="mb-4 border-green-500 text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your password has been changed successfully. Please log in with your new password.
                  </AlertDescription>
                </Alert>
              )}

              {isImpersonationLogin && (
                <Alert className="mb-4 border-blue-500 text-blue-700">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Super Admin Impersonation: Logging in as {decodeURIComponent(impersonationEmail!)}
                  </AlertDescription>
                </Alert>
              )}
              
            
              <div className="flex flex-col space-y-2 text-left">
                <h1 className="text-2xl font-semibold tracking-tight">
                  {isImpersonationLogin ? "Super Admin Login" : "Login"}
                </h1>
                <p className="text-[10px] md:text-[15px] text-muted-foreground">
                  {isImpersonationLogin 
                    ? "Authenticating impersonation request..." 
                    : "Choose your preferred login method below"
                  }
                </p>
              </div>

              {isImpersonationLogin ? (
                <UserAuthForm 
                  impersonationEmail={impersonationEmail}
                  impersonationCode={impersonationCode}
                />
              ) : (
                <Tabs defaultValue="password" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="password">Password</TabsTrigger>
                    <TabsTrigger value="otp">OTP</TabsTrigger>
                  </TabsList>
                  <TabsContent value="password">
                    <UserAuthForm
                      email={sharedEmail}
                      onEmailChange={setSharedEmail}
                    />
                  </TabsContent>
                  <TabsContent value="otp">
                    <OtpAuthForm
                      email={sharedEmail}
                      onEmailChange={setSharedEmail}
                    />
                  </TabsContent>
                </Tabs>
              )}

              <p className="px-8 text-center text-[10px] md:text-sm text-muted-foreground">
                By clicking login, you agree to our{" "}
                <Link
                  href="/terms"
                  target="_blank"
                  className="underline underline-offset-4 hover:text-primary"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="/privacy-policy"
                  target="_blank"
                  className="underline underline-offset-4 hover:text-primary"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}